import _ from 'lodash';
import { batch } from 'react-redux';
import template from 'url-template';

import {
    FETCH_ROLES,
    FETCH_SCORING,
    SCORING_LOAD_BEGIN,
    SCORING_LOAD_END,
    SCORING_LOAD_ERROR,
    TOGGLE_SCORING_LOAD_BEGIN,
    TOGGLE_SCORING_LOAD_END,
    TOGGLE_SCORING_LOAD_ERROR,
    SET_ROLE,
    TOGGLE_SCORING
}  from '../actions/actionTypes';

import { handleNetworkError, enqueueSnackbar } from './applicationMessageActions';

import awsHeader from '../helpers/awsAuth';
import { getErrorMessage } from '../helpers/errors';
import k2aBaseApi from '../apis/key2act';
import lambdaBaseApi from '../apis/lambda';

import { fetchCompanyInfo } from './companyInfoActions';

const AccountsUrl = template.parse('/authorization-roles/{roleId}/accounts');
const AuthorizationRolesUrl = template.parse('/companies/{companyId}/authorization-roles{?searchFilter,limit}');
const ResendInviteUrl = template.parse('/authorization-roles/{roleId}/accounts/resend-invitation');
const SpecificAccountUrl = template.parse('/authorization-roles/{roleId}/accounts/{accountId}');
const SpecificAuthorizationRoleUrl = template.parse('/authorization-roles/{roleId}');
const ScoringUrl = template.parse('/scoring/logs{?startTimestamp}{&endTimestamp}');
const ToggleScoringUrl = template.parse('/companies/{companyId}');
const CompanyDetailsUrl = template.parse('/companies/{companyId}');

const DEFAULT_PAGE_LIMIT = 100;

export const createAccount = (roleId, body, options = {}) => async (dispatch) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.post(
            AccountsUrl.expand({
                roleId,
                ...options
            }),
            body,
            header
        );

        dispatch(enqueueSnackbar('The user has been invited.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const createAuthorizationRole = (companyId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.post(
            AuthorizationRolesUrl.expand({
                companyId,
                ...options
            }),
            body,
            header
        );

        const response = await k2aBaseApi.get(
            AuthorizationRolesUrl.expand({
                companyId,
                ...options
            }),
            header
        );

        const payload = _.chain(response).get('data.data', []).find(role => role.name === body.name).value();

        batch(() => {
            dispatch(enqueueSnackbar('Role created successfully.', 'success'));
            dispatch(fetchAuthorizationRoles(companyId));
            dispatch(setRole(payload));
        });

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const deleteAccount = (roleId, accountId, options = {}) => async dispatch => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.delete(
            SpecificAccountUrl.expand({ roleId, accountId }),
            {
                headers: header.headers
            }
        );

        batch(() => {
            dispatch(enqueueSnackbar('User was removed successfully.', 'success'));
        });

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const deleteAuthorizationRole = (roleId, companyId, options = {}) => async dispatch => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.delete(
            SpecificAuthorizationRoleUrl.expand({ roleId }),
            {
                headers: header.headers
            }
        );

        batch(() => {
            dispatch(enqueueSnackbar('Role deleted successfully.', 'success'));
            dispatch(fetchAuthorizationRoles(companyId));
        });

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const fetchAuthorizationRoles = (companyId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    options.limit || (options.limit = DEFAULT_PAGE_LIMIT);

    try {
        const response = await k2aBaseApi.get(
            AuthorizationRolesUrl.expand({
                companyId,
                ...options
            }),
            header
        );

        const payload = _.get(response, 'data.data', []).sort((a, b) => _sorter(a, b));

        dispatch({
            type: FETCH_ROLES,
            payload
        });

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const resendInvitation = (roleId, body, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.post(
            ResendInviteUrl.expand({
                roleId,
                ...options
            }),
            body,
            header
        );

        dispatch(enqueueSnackbar('The user was re-invited successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const setRole = payload => ({
    type: SET_ROLE,
    payload
});


export const updateAccount = (roleId, body, options = {}) => async (dispatch) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.put(
            AccountsUrl.expand({
                roleId,
                ...options
            }),
            body,
            header
        );

        dispatch(enqueueSnackbar('User\'s Role updated successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const updateAuthorizationRole = (roleId, companyId, body, options = {}) => async (dispatch) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.put(
            SpecificAuthorizationRoleUrl.expand({
                roleId,
                ...options
            }),
            body,
            header
        );

        batch(() => {
            dispatch(enqueueSnackbar('Role updated successfully.', 'success'));
            dispatch(fetchAuthorizationRoles(companyId));
        });

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

export const fetchScoring = (node, options = {}) => async (dispatch, getState) => {
    const headers = await awsHeader();

    const startTimestamp = node.startTimestamp;
    const endTimestamp = node.endTimestamp;

    const scoringDataRequest = {
        startTimestamp,
        endTimestamp
    };

    try {
        dispatch(beginLoading());
        const response = await lambdaBaseApi.get(
            ScoringUrl.expand({
                ...scoringDataRequest
            }),
            headers
        );
        const payload = response.data;
        dispatch({
            type: FETCH_SCORING,
            payload
        });
    } catch (error) {
        dispatch(errorLoading(getErrorMessage(error)));
        dispatch(handleNetworkError(error));
    }
    finally {
        dispatch(endLoading());
    }
};

const beginLoading = () => ({
    type:    SCORING_LOAD_BEGIN,
    payload: null
});

const endLoading = () => ({
    type:    SCORING_LOAD_END,
    payload: null
});

const beginToggleScoringLoading = () => ({
    type:    TOGGLE_SCORING_LOAD_BEGIN,
    payload: null
});

const endToggleScoringLoading = () => ({
    type:    TOGGLE_SCORING_LOAD_END,
    payload: null
});

const errorLoading = (error) => ({
    type:    SCORING_LOAD_ERROR,
    payload: error
});

const errorToggleScoring = (error) => ({
    type:    TOGGLE_SCORING_LOAD_ERROR,
    payload: error
});

export const toggleScoring = (companyInfo = {}) => async (dispatch, getState) => {
    const headers = await awsHeader();

    try {
        dispatch(beginToggleScoringLoading());
        const rawCompanyData = await k2aBaseApi.get(
            CompanyDetailsUrl.expand({
                companyId: companyInfo.companyId
            }),
            headers
        );

        let body = rawCompanyData.data;
        body.scoringEnabled = !rawCompanyData.data.scoringEnabled;
        const response = await k2aBaseApi.put(
            ToggleScoringUrl.expand({
                companyId: companyInfo.companyId,
            }),
            body,
            headers
        );
        const payload = response.data;
        dispatch({
            type: TOGGLE_SCORING,
            payload
        });

        const toggleCompanyData = await k2aBaseApi.get(
            CompanyDetailsUrl.expand({
                companyId: companyInfo.companyId
            }),
            headers
        );
        const formattedCompany = {company: {...toggleCompanyData.data}};

        await dispatch(fetchCompanyInfo(formattedCompany));
    } catch (error) {
        dispatch(errorToggleScoring(getErrorMessage(error)));
        dispatch(handleNetworkError(error));
    }
    finally {
        dispatch(endToggleScoringLoading());
    }
};


const _sorter = (a, b) => {
    let roleA = a.name.toUpperCase();
    let roleB = b.name.toUpperCase();

    if (roleA < roleB) {
        return -1;
    }
    if (roleA > roleB) {
        return 1;
    }

    return 0;
};
